import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { Picker } from "emoji-mart";
// hooks
import useClickOutside from "../../hooks/useClickOutside";

// Utils
import {
  isEmpty,
  setItem,
  getItem,
  removeItem,
  sentenceCaseTyping
} from "../../utils";

// constants
import { NOTIFICATION_PRESETS } from "../../constants/localStorage/localStorage.constants";

// styles
import { ChatInputStyles } from "./chatInput.styles";
import "emoji-mart/css/emoji-mart.css";
import { toast } from "react-toastify";

const BellIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2a6 6 0 0 0-6 6v3.586l-1.707 1.707A1 1 0 0 0 5 15h14a1 1 0 0 0 .707-1.707L18 11.586V8a6 6 0 0 0-6-6zm0 20a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22z" />
  </svg>
);

const SmileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
    <line x1="9" y1="9" x2="9.01" y2="9" />
    <line x1="15" y1="9" x2="15.01" y2="9" />
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" fill="currentColor" stroke="none" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const ChatInput = ({ serviceId, mode, setMode }) => {
  const { socket } = useSelector(state => state.app);
  const [input, setInput] = useState("");
  const [isEmojisOpen, setIsEmojisOpen] = useState(false);
  const [presets, setPresets] = useState(getItem(NOTIFICATION_PRESETS) || []);
  const picker = useRef(null);
  const inputRef = useRef(null);
  const isNotif = mode === "notification";

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [mode]);

  // Auto-grow the textarea up to a max height; reset first so it shrinks when
  // lines are removed.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [input]);

  const sendMessage = async (overrideText, overrideType) => {
    const body = overrideText !== undefined ? overrideText : input;
    if (isEmpty(body) || isEmpty(socket)) return;
    const messageData = {
      service_id: `${serviceId}`,
      message_id: crypto.randomUUID(),
      body,
      type: overrideType || (isNotif ? "system" : "text")
    };
    await socket.emit("message", messageData);
    setInput("");
  };

  const addEmoji = emoji => {
    if ("native" in emoji) {
      setInput(`${input}${emoji.native}`);
      setIsEmojisOpen(false);
    }
  };

  const dismissPicker = () => setIsEmojisOpen(false);
  useClickOutside([picker], dismissPicker);

  const savePreset = () => {
    const trimmed = input.trim();
    if (isEmpty(trimmed)) {
      toast.error("Preset cannot be empty.");
      return;
    }
    if (presets.length >= 10) {
      toast.error("You can have maximum 10 presets");
      return;
    }
    if (presets.includes(trimmed)) return;
    const next = [trimmed, ...presets];
    setPresets(next);
    setItem(NOTIFICATION_PRESETS, next);
  };

  const removePreset = i => {
    const next = presets.filter((_, idx) => idx !== i);
    if (isEmpty(next)) {
      setPresets([]);
      removeItem(NOTIFICATION_PRESETS);
      return;
    }
    setPresets(next);
    setItem(NOTIFICATION_PRESETS, next);
  };

  const sendPreset = text => {
    sendMessage(text, "system");
  };

  const trimmed = input.trim();
  const canSave = trimmed.length > 0 && !presets.includes(trimmed);

  return (
    <ChatInputStyles $isNotif={isNotif} $hasInputValue={!!input}>
      <div
        className={`presetRow ${isNotif ? "isOpen" : ""}`}
        aria-hidden={!isNotif}
      >
        <div className="presetRowInner">
          <div className="presetTopRow">
            <span className="presetLabel">Presets</span>
            {canSave && (
              <button
                type="button"
                className="presetSave"
                onClick={savePreset}
                tabIndex={isNotif ? 0 : -1}
              >
                <PlusIcon /> Save as preset
              </button>
            )}
          </div>
          <div className="presetChips">
            {presets.length === 0 ? (
              <span className="presetEmpty">
                No presets yet. Type a message and tap “Save as preset”.
              </span>
            ) : (
              presets.map((p, i) => (
                <span
                  key={i}
                  className="presetChip"
                  onClick={() => sendPreset(p)}
                  title="Send as notification"
                >
                  <span className="presetChipText">{p}</span>
                  <button
                    type="button"
                    className="presetX"
                    onClick={e => {
                      e.stopPropagation();
                      removePreset(i);
                    }}
                    aria-label="Delete preset"
                    tabIndex={isNotif ? 0 : -1}
                  >
                    <CloseIcon />
                  </button>
                </span>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="inputRow">
        <button
          type="button"
          className={`modeBtn ${isNotif ? "isActive" : ""}`}
          onClick={() => setMode(isNotif ? "text" : "notification")}
          title={isNotif ? "Switch to chat" : "Send a notification"}
          aria-label={isNotif ? "Switch to chat" : "Send a notification"}
        >
          <BellIcon />
        </button>

        <div className="inputShell">
          <textarea
            ref={inputRef}
            className="chatTextInput"
            id="chatTextInput"
            rows={1}
            autoCapitalize="sentences"
            placeholder={
              isNotif ? "Send a notification to the driver…" : "Write a message…"
            }
            value={input}
            onChange={e =>
              setInput(
                sentenceCaseTyping(input, e.target.value, e.target.selectionStart)
              )
            }
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
          />
          {!isNotif && (
            <div ref={picker} className="emojiWrap">
              <button
                type="button"
                className="emojiBtn"
                onClick={() => setIsEmojisOpen(!isEmojisOpen)}
                aria-label="Emoji"
              >
                <SmileIcon />
              </button>
              {isEmojisOpen && (
                <Picker
                  useButton={false}
                  set="facebook"
                  title=""
                  emoji=""
                  native={true}
                  style={{ position: "absolute", bottom: 40, right: 0 }}
                  onSelect={addEmoji}
                />
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          className={`sendBtn ${input ? "isReady" : ""}`}
          onClick={() => sendMessage()}
          aria-label="Send"
        >
          <SendIcon />
        </button>
      </div>

      <div className="modeHint">
        {isNotif ? (
          <>
            <InfoIcon />
            <span>
              Notifications appear as a system message and push to the driver's
              phone.
            </span>
          </>
        ) : (
          <span className="hintMuted">
            Press <kbd>Enter</kbd> to send · <kbd>Shift+Enter</kbd> for newline
          </span>
        )}
      </div>
    </ChatInputStyles>
  );
};

export default ChatInput;
